import { useState, useEffect } from 'react'
import { Wifi, WifiOff, AlertTriangle } from 'lucide-react'

export function ConnectionStatus() {
  const [status, setStatus] = useState<'online' | 'offline' | 'connecting'>('offline')
  const [connectionCount, setConnectionCount] = useState(0)

  useEffect(() => {
    // TODO: Replace with real P2P network status monitoring
    const interval = setInterval(() => {
      // Simulate connection status changes for demo
      const random = Math.random()
      if (random > 0.8) {
        setStatus('connecting')
      } else if (random > 0.3) {
        setStatus('online')
        setConnectionCount(Math.floor(Math.random() * 5))
      } else {
        setStatus('offline')
        setConnectionCount(0)
      }
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  const getStatusColor = () => {
    switch (status) {
      case 'online': return 'text-success-600'
      case 'connecting': return 'text-warning-600'
      case 'offline': return 'text-error-600'
      default: return 'text-secondary-600'
    }
  }

  const getStatusBg = () => {
    switch (status) {
      case 'online': return 'bg-success-50 border-success-200'
      case 'connecting': return 'bg-warning-50 border-warning-200'
      case 'offline': return 'bg-error-50 border-error-200'
      default: return 'bg-secondary-50 border-secondary-200'
    }
  }

  const getStatusIcon = () => {
    switch (status) {
      case 'online':
        return <Wifi className="w-4 h-4" />
      case 'connecting':
        return <AlertTriangle className="w-4 h-4" />
      case 'offline':
        return <WifiOff className="w-4 h-4" />
      default:
        return <WifiOff className="w-4 h-4" />
    }
  }

  const getStatusText = () => {
    switch (status) {
      case 'online':
        return `Connected • ${connectionCount} peer${connectionCount !== 1 ? 's' : ''}`
      case 'connecting':
        return 'Connecting to network...'
      case 'offline':
        return 'Offline • No connections'
      default:
        return 'Unknown status'
    }
  }

  return (
    <div
      className={`border-t px-3 sm:px-4 py-2 ${getStatusBg()} max-w-full overflow-hidden`}
      style={{
        paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom))',
      }}
    >
      <div className="flex items-center justify-between min-w-0">
        <div className={`flex items-center space-x-2 ${getStatusColor()} flex-1 min-w-0`}>
          {getStatusIcon()}
          <span className="text-xs sm:text-sm font-medium truncate">
            {getStatusText()}
          </span>
        </div>

        {status === 'online' && (
          <div className="flex space-x-1 flex-shrink-0 ml-2">
            {[...Array(Math.min(connectionCount, 3))].map((_, i) => (
              <div
                key={i}
                className="w-2 h-2 bg-success-500 rounded-full animate-pulse-soft"
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
            {connectionCount > 3 && (
              <span className="text-xs text-success-600 ml-1">
                +{connectionCount - 3}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}